import { useEffect, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import { useQuery } from "@tanstack/react-query";
import { useUser } from "@context/contexts";
import { useApi } from "@hooks/useApi";
import PropTypes from "prop-types";
import { todayIso } from "@utils/index";
import YearHeatmap from "@components/admin-components/YearHeatmap";

const getColor = (date, stats) => {
    const today = todayIso();
    if (!stats) return "#ffffff";
    
    if (stats.completed) return "#4caf50";
    if (date > today) return "#d3d3d3";
    
    const { puzzles = 0, games = 0 } = stats;
    if (puzzles >= stats.puzzlesNeeded) return "#ffeb3b";
    if (games >= stats.gamesNeeded) return "#2196f3";

    return "#ffffff";
};

const DailyHeatmap = ({ lichessId = null }) => {
    const { token } = useUser();
    const api = useApi();

    const { data: progressData = [], isError } = useQuery({
        queryKey: ["tasks-daily", lichessId || "me", token],
        queryFn: async () => {
            const endpoint = lichessId ? `tasks/daily/${lichessId}` : "tasks/daily";
            const result = await api.get(endpoint);
            if (!result?.ok || !Array.isArray(result.data?.progress)) {
                throw new Error("Некорректный ответ сервера");
            }
            return result.data.progress;
        },
        enabled: Boolean(token),
        staleTime: 60 * 1000,
    });

    useEffect(() => {
        if (isError) {
            toast.error("Не удалось загрузить статистику");
        }
    }, [isError]);

    const { statistics, allDates } = useMemo(() => {
        const mapped = {};
        const dates = [];
        progressData.forEach((x) => {
            mapped[x.date] = x;
            dates.push(x.date);
        });
        return { statistics: mapped, allDates: dates };
    }, [progressData]);

    const formatDate = isoDate => {
        const [year, month, day] = isoDate.split("-");
        return `${day}.${month}.${year}`;
    };
    const getText = useCallback((iso) => {
        const stats = statistics[iso];
        if (stats && Object.hasOwn(stats, "completed")) {
            const gamesText = `${stats?.games}/${stats?.gamesNeeded} игр`;
            const puzzlesText = `${stats?.puzzles}/${stats?.puzzlesNeeded} задач`;
            const streakText = `серия ${stats?.streak || 0}`;
            return `${formatDate(iso)}: ${gamesText}, ${puzzlesText}, ${streakText}`;
        }
        return `${formatDate(iso)}: нет данных`;
    }, [statistics]);
    const getCellStyle = useCallback((iso) => {
        const stats = statistics[iso];
        return {
            backgroundColor: getColor(iso, stats),
            border: stats ? "1px solid #ccc" : "none",
        };
    }, [statistics]);

    return (
        <YearHeatmap
            title="Ежедневные цели"
            dates={allDates}
            getCellTitle={getText}
            getCellStyle={getCellStyle}
        />
    );
};

DailyHeatmap.propTypes = {
    lichessId: PropTypes.string
};

export default DailyHeatmap;
